import { useMutation } from "@tanstack/react-query";

import { toast } from "~/components/ui/use-toast";
import type { GroupSuggestionResponse } from "~/features/parts-grouping/types";

// AIDEV-NOTE: React Query mutation hook for triggering part group suggestions

interface SuggestPartGroupsVariables {
    sessionId: string;
    groupingType: "CORE_WOUND" | "AREA_OF_LIFE";
}

/**
 * Hook to trigger part group suggestions for a session.
 * Returns suggestions for which group each part should be placed in.
 */
export function useSuggestPartGroups() {
    return useMutation<
        GroupSuggestionResponse,
        Error,
        SuggestPartGroupsVariables
    >({
        mutationFn: async ({ sessionId, groupingType }) => {
            const response = await fetch(`/api/parts/suggest-groups`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                credentials: "include",
                body: JSON.stringify({ sessionId, groupingType }),
            });

            if (!response.ok) {
                let errorData;
                try {
                    errorData = await response.json();
                } catch {
                    throw new Error("Failed to suggest part groups");
                }
                throw new Error(
                    errorData.error || "Failed to suggest part groups",
                );
            }

            const data = await response.json();
            return data;
        },
        onError: (error) => {
            console.error("Failed to suggest part groups:", error);
            if (typeof window !== "undefined") {
                toast({
                    title: "Error",
                    description:
                        error.message || "Failed to suggest part groups",
                    variant: "destructive",
                });
            }
        },
    });
}

interface AcceptGroupSuggestionVariables {
    partId: string;
    groupingType: "CORE_WOUND" | "AREA_OF_LIFE";
    groupName: string;
    isNewGroup: boolean;
    groupDescription?: string;
}

/**
 * Hook to accept a group suggestion for a part.
 */
export function useAcceptGroupSuggestion() {
    return useMutation<
        { success: boolean },
        Error,
        AcceptGroupSuggestionVariables
    >({
        mutationFn: async ({
            partId,
            groupingType,
            groupName,
            isNewGroup,
            groupDescription,
        }) => {
            const response = await fetch(`/api/parts/accept-group-suggestion`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                credentials: "include",
                body: JSON.stringify({
                    partId,
                    groupingType,
                    groupName,
                    isNewGroup,
                    groupDescription,
                }),
            });

            if (!response.ok) {
                let errorData;
                try {
                    errorData = await response.json();
                } catch {
                    throw new Error("Failed to accept group suggestion");
                }
                throw new Error(
                    errorData.error || "Failed to accept group suggestion",
                );
            }

            const data = await response.json();
            return data;
        },
        onError: (error) => {
            console.error("Failed to accept group suggestion:", error);
            if (typeof window !== "undefined") {
                toast({
                    title: "Error",
                    description:
                        error.message || "Failed to accept group suggestion",
                    variant: "destructive",
                });
            }
        },
    });
}

interface DismissPartSuggestionVariables {
    partId: string;
    groupingType: "CORE_WOUND" | "AREA_OF_LIFE";
}

/**
 * Hook to dismiss a part from group suggestions ("this is a new part").
 * Dismissed parts won't appear in future suggestion dialogs.
 */
export function useDismissPartSuggestion() {
    return useMutation<
        { success: boolean },
        Error,
        DismissPartSuggestionVariables
    >({
        mutationFn: async ({ partId, groupingType }) => {
            const response = await fetch(`/api/parts/dismiss-suggestion`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                credentials: "include",
                body: JSON.stringify({
                    partId,
                    groupingType,
                }),
            });

            if (!response.ok) {
                let errorData;
                try {
                    errorData = await response.json();
                } catch {
                    throw new Error("Failed to dismiss part from suggestions");
                }
                throw new Error(
                    errorData.error ||
                        "Failed to dismiss part from suggestions",
                );
            }

            const data = await response.json();
            return data;
        },
        onError: (error) => {
            console.error("Failed to dismiss part from suggestions:", error);
            if (typeof window !== "undefined") {
                toast({
                    title: "Error",
                    description:
                        error.message ||
                        "Failed to dismiss part from suggestions",
                    variant: "destructive",
                });
            }
        },
    });
}
